import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { formatApiError } from "../lib/api";
import { LOGO_URL } from "../lib/brand";
import { Loader2, Lock, Sprout, Tractor, Store, GraduationCap, Leaf, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MusicToggle from "../components/MusicToggle";
import { audio } from "../lib/audio";
import { toast } from "sonner";

const HERO = "/hero-farmer.webp";

const OCC = [
  { key: "Farmer", icon: Sprout, hi: "किसान", en: "Farmer" },
  { key: "FPO", icon: Users, hi: "FPO/समूह", en: "FPO/Group" },
  { key: "Trader", icon: Store, hi: "व्यापारी", en: "Trader" },
  { key: "Student", icon: GraduationCap, hi: "छात्र/शोधकर्ता", en: "Student" },
  { key: "Advisor", icon: Leaf, hi: "कृषि सलाहकार", en: "Agri Advisor" },
  { key: "Other", icon: Tractor, hi: "अन्य", en: "Other" },
];

const TX = {
  हिंदी: { heading: "आपके खेत का साथी, बस एक कदम दूर 🌾", login: "लॉगिन", signup: "नया खाता", welcome: "फिर से स्वागत है", create: "अपना खाता बनाइए", name: "नाम", namePh: "मधुर मिश्रा", phone: "मोबाइल नंबर या ईमेल", pwd: "पासवर्ड", village: "गांव / शहर", occ: "आप क्या हैं?", or: "या", google: "Google से जारी रखें", demo: "डेमो देखें", trust: "आपकी जानकारी पूरी तरह सुरक्षित है", phoneHint: "10 अंक, 6-9 से शुरू", testis: ["“मंडी जाने से पहले भाव यहीं देख लेता हूँ।” — रमेश, नीमच", "“ओले की चेतावनी ने मेरी फसल बचाई।” — सीता, मंदसौर", "“AI डॉक्टर ने रोग तुरंत पहचाना।” — मोहन, रतलाम"] },
  English: { heading: "Your farm's companion, just one step away 🌾", login: "Login", signup: "Sign up", welcome: "Welcome back", create: "Create your account", name: "Name", namePh: "Madhur Mishra", phone: "Mobile number or email", pwd: "Password", village: "Village / City", occ: "What are you?", or: "or", google: "Continue with Google", demo: "Try Demo", trust: "Your information is fully secure", phoneHint: "10 digits, starts 6-9", testis: ["“I check rates here before going to the mandi.” — Ramesh, Neemuch", "“The hail alert saved my crop.” — Sita, Mandsaur", "“AI Doctor spotted the disease instantly.” — Mohan, Ratlam"] },
  Hinglish: { heading: "Aapke khet ka saathi, bas ek kadam door 🌾", login: "Login", signup: "Naya khata", welcome: "Wapas swagat hai", create: "Apna khata banaiye", name: "Naam", namePh: "Madhur Mishra", phone: "Mobile number ya email", pwd: "Password", village: "Gaon / Shehar", occ: "Aap kya hain?", or: "ya", google: "Google se jaari rakhein", demo: "Demo dekhein", trust: "Aapki jaankari poori tarah surakshit hai", phoneHint: "10 digit, 6-9 se shuru", testis: ["“Mandi jaane se pehle bhav yahin dekh leta hoon.” — Ramesh", "“Ole ki chetavni ne fasal bachai.” — Sita", "“AI Doctor ne rog turant pehchana.” — Mohan"] },
};

export default function Auth() {
  const { login, register, demoLogin, guestLogin } = useAuth();
  const { language } = useApp();
  const x = TX[language] || TX["हिंदी"];
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", identifier: "", password: "", village: "", occupation: "Farmer" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ti, setTi] = useState(0);

  useEffect(() => { const id = setInterval(() => setTi((i) => (i + 1) % x.testis.length), 3500); return () => clearInterval(id); }, [x.testis.length]);

  // welcome song plays on the auth page; hard-stops on leave
  useEffect(() => { audio.playWelcome(); return () => audio.stopWelcome(); }, []);

  const isPhone = /^\d+$/.test(form.identifier.trim());
  const phoneOk = !isPhone || /^[6-9]\d{9}$/.test(form.identifier.trim());

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      let user;
      if (mode === "login") user = await login(form.identifier, form.password);
      else user = await register({ ...form, language });
      toast.success(x.welcome);
      navigate(user?.onboarded ? "/" : "/onboarding");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const doDemo = async () => {
    setLoading(true);
    try { await demoLogin(); toast.success("Demo farm loaded"); navigate("/"); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doGuest = async () => {
    setLoading(true);
    try { await guestLogin(); navigate("/market"); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[55%_45%] font-deva">
      {/* top-right mute / unmute for the welcome song */}
      <MusicToggle className="fixed top-4 right-4 z-[95]" />
      {/* LEFT — farmer photo + rotating testimonial */}
      <div className="relative h-40 lg:h-auto overflow-hidden">
        <img src={HERO} alt="farm" className="absolute inset-0 h-full w-full object-cover object-[right_top]" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/70 lg:bg-gradient-to-t lg:from-black/50 lg:via-black/10 lg:to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 hidden lg:block text-white">
          <img src={LOGO_URL} alt="" className="h-14 w-14 rounded-2xl object-contain bg-white/95 mb-4 shadow-lg" />
          <AnimatePresence mode="wait">
            <motion.p key={ti} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}
              className="text-xl font-semibold max-w-md drop-shadow-lg">{x.testis[ti]}</motion.p>
          </AnimatePresence>
        </div>
      </div>

      {/* RIGHT — glass auth card */}
      <div className="flex items-center justify-center p-5 sm:p-8 bg-gradient-to-b from-emerald-50/60 to-white min-h-[calc(100vh-10rem)] lg:min-h-screen">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-sm rounded-3xl bg-[rgba(255,255,255,0.9)] backdrop-blur-[8px] border border-black/5 shadow-2xl p-6">
          <img src={LOGO_URL} alt="Agro Madat" className="h-12 w-12 rounded-2xl object-contain bg-white shadow mx-auto mb-3" />
          <h1 className="text-lg font-bold text-center mb-1 text-[#1a1a1a] leading-snug">{x.heading}</h1>

          <div className="flex gap-1 p-1 bg-black/5 rounded-xl my-5">
            {["login", "signup"].map((m) => (
              <button key={m} onClick={() => { setMode(m); setErr(""); }} data-testid={`tab-${m}`}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === m ? "bg-white shadow-sm text-primary" : "text-muted-foreground"}`}>
                {m === "login" ? x.login : x.signup}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3.5">
            <AnimatePresence>
              {mode === "signup" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3.5 overflow-hidden">
                  <Input data-testid="input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required={mode === "signup"} className="rounded-xl h-11 bg-white" placeholder={x.name + " — " + x.namePh} />
                  <Input data-testid="input-village" value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} className="rounded-xl h-11 bg-white" placeholder={x.village} />
                </motion.div>
              )}
            </AnimatePresence>

            {/* identifier with +91 chip when numeric */}
            <div className="flex items-center rounded-xl h-11 bg-white border overflow-hidden focus-within:ring-2 focus-within:ring-primary/40 transition">
              {isPhone && <span className="px-2.5 text-sm font-semibold text-muted-foreground border-r bg-black/[0.03] h-full flex items-center">+91</span>}
              <input data-testid="input-identifier" value={form.identifier} inputMode="text"
                onChange={(e) => setForm({ ...form, identifier: e.target.value })} required
                className="flex-1 px-3 h-full bg-transparent outline-none text-[15px]" placeholder={x.phone} />
            </div>
            {isPhone && !phoneOk && form.identifier.length > 0 && <p className="text-xs text-amber-600 -mt-1.5">{x.phoneHint}</p>}

            <Input data-testid="input-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="rounded-xl h-11 bg-white" placeholder={x.pwd} />

            {/* occupation cards */}
            <AnimatePresence>
              {mode === "signup" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">{x.occ}</p>
                  <div className="grid grid-cols-3 gap-2" data-testid="occupation-cards">
                    {OCC.map(({ key, icon: Icon, hi, en }) => (
                      <button type="button" key={key} onClick={() => setForm({ ...form, occupation: key })} data-testid={`occ-${key}`}
                        className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[11px] font-medium transition-all active:scale-95 ${form.occupation === key ? "border-primary bg-primary/10 text-primary" : "border-black/10 text-muted-foreground hover:bg-black/[0.03]"}`}>
                        <Icon className="h-5 w-5" />{language === "हिंदी" ? hi : en}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {err && <p className="text-sm text-destructive" data-testid="auth-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="submit-auth" className="w-full h-11 rounded-xl text-base bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? x.login : x.signup)} →
            </Button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">{x.or}</span><div className="flex-1 h-px bg-border" />
          </div>
          <div className="space-y-2.5">
            <Button variant="outline" onClick={googleLogin} data-testid="google-login" className="w-full h-11 rounded-xl bg-white">
              <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" />{x.google}
            </Button>
            <Button variant="ghost" onClick={doDemo} data-testid="demo-login" className="w-full h-11 rounded-xl">{x.demo} 🌾</Button>
            <button onClick={doGuest} data-testid="guest-browse" className="w-full text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 mt-1">बिना लॉगिन देखें · Browse as guest</button>
          </div>

          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-5"><Lock className="h-3 w-3" />{x.trust}</p>
        </motion.div>
      </div>
    </div>
  );
}
